import { withNamespaces } from "react-i18next"
import {
	Col,
	Row,
} from "reactstrap"
import { useMutation, useQuery } from "@tanstack/react-query";
import { toast } from "react-toastify";
import {
    SimpleQuestionnaire,
    useProgress
} from '@smartintegrity/questionnaire';
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { connect } from "react-redux";
import ProcessingActivityService from "src/modules/data-protection/apis/ProcessingActivityService";
import CommonService from "src/modules/data-protection/apis/CommonService";
import { createQuestionnaireAiSuggestionExtension } from "../../components/QuestionnaireAiSuggestionExtension";
import { createQuestionnaireAssessmentGapExtension } from "../../components/QuestionnaireAssessmentGapExtension";
import { createQuestionnaireFocusMarkersExtension } from "../../components/QuestionnaireFocusMarkersExtension";
import { createQuestionnaireGapExtension } from "../../components/QuestionnaireGapExtension";
import { composeQuestionExtensions } from "../../components/composeQuestionExtensions";
import AssessmentActions, {
    submitQuestionnaireForm
} from "../../components/AssessmentActions";
import AssessmentSaveButton from "../../components/AssessmentSaveButton";
import { buildComplianceObligationsMap } from "src/modules/data-protection/helpers/complianceObligations";
import {
    focusAssessmentQuestionElement,
    hasAssessmentQuestionFocus,
} from "../../helpers/assessmentQuestionFocus";

const QuestionsLoadingPlaceholder = () => {
    return (
        <div>
            <Row className="mb-2">
                <Col lg="12">
                    <p style={{width: '100%', height: '55px'}} className={`dt-field dt-skeleton`}></p>
                </Col>
            </Row>
        </div>
    );
}

const ProcessingQuestionnaire = ({
    index,
    report,
    token,
    softwareId,
    complianceObligations = [],
    questionFocus = null,
    onSaved,
    onProgressChange,
    t
}) => {
    const [ questionnaireCompleted, setQuestionnaireCompleted ] = useState(false);
    const hasTriggeredQuestionFocusRef = useRef(false);

    const fetchCountriesQuery = useQuery({
        queryKey: ["dpdr-processing-activity-countries"],
        queryFn: async () => {
            const service = CommonService.getInstance();

            return await service.countries();
        },
        cacheTime: 0,
        refetchOnWindowFocus: false,
        onError: (error) => {
            if (process.env.NODE_ENV === "development") console.error(error);

            toast(t("An error occurred while fetching countries."), {
                type: "error"
            });
        },
    });

    const {
        control: controlQuestionnaireProgress,
        register: registerQuestionnaireForProgress
    } = useProgress();

    const extraData = useMemo(() => {
        return {
            countries: {
                isFetching: fetchCountriesQuery.isFetching,
                data: (fetchCountriesQuery.data || []).map((country) => {
                    return {
                        ...country,
                        title: country.name
                    }
                })
            }
        }
    }, [
        fetchCountriesQuery.data,
        fetchCountriesQuery.isFetching
    ]);

    const updateActivityMutation = useMutation({
        mutationFn: async (payload) => {
            const service = ProcessingActivityService.getInstance();
            return await service.updateActivityQuestionnaire(payload)
        },
        onSuccess() {
            toast(t("Report Updated"), {
                type: 'success'
            });
            onSaved && onSaved();
        },
        onError(error) {
            if (process.env.NODE_ENV === 'development') console.error(error)
            toast(t('Failed to update'), {
                type: "error"
            })
        }
    });

    const initActivityMutation = useMutation({
        mutationFn: async (payload) => {
            const service = ProcessingActivityService.getInstance();
            return await service.initActivityQuestionnaire(payload)
        },
        onSuccess() {
            toast(t("Report Updated"), {
                type: 'success'
            });
            onSaved && onSaved();
        },
        onError(error) {
            if (process.env.NODE_ENV === 'development') console.error(error)
            toast(t('Failed to update'), {
                type: "error"
            })
        }
    });

    const questionnaireRef = useRef();

    const saveInProgress = updateActivityMutation.isLoading || initActivityMutation.isLoading;

    const save = useCallback(() => {
        submitQuestionnaireForm(questionnaireRef.current);
    }, []);

    useEffect(() => {
        if(report?.case_id){
            registerQuestionnaireForProgress(report.case_id);
        }
    }, [ 
        report?.case_id,
        registerQuestionnaireForProgress
    ]);

    useEffect(() => {
        hasTriggeredQuestionFocusRef.current = false;
    }, [questionFocus, report.case_id]);

    const questionnaireStatusChangeCallback = useCallback((_currentPayload, newStatus) => {
        setQuestionnaireCompleted(newStatus);

        if (
            hasTriggeredQuestionFocusRef.current
            || !hasAssessmentQuestionFocus(questionFocus)
        ) {
            return;
        }

        hasTriggeredQuestionFocusRef.current = true;
        focusAssessmentQuestionElement(questionFocus, {
            delayMs: 200,
            questions: report.questions,
        });
    }, [questionFocus, report.questions]);

    const handleSubmitQuestionnaire = useCallback((payload) => {
        if(report?.case_id){
            if(!report.is_empty){
                updateActivityMutation.mutate({
                    software: softwareId,
                    questionAnswers: payload,
                    hardware: null,
                    reportCase: report.case_id,
                    questionnaireType: 'processing_activity'
                });
            }
            else{
                initActivityMutation.mutate({
                    questionAnswers: payload,
                    reportCase: report.case_id
                })
            }
        }
    }, [ 
        report,
        updateActivityMutation,
        initActivityMutation,
        softwareId
    ]);

    const settings = useMemo(() => {
        return {
            panelSettings: {
                showNumber: true,
                showProgressBar: false,
                prefix: `${index + 1}.`,
            }, 
            /**
             * Save is rendered below the panel, outside the library's form.
             */
            buttonsSettings: {
                submit: {
                    display: false
                },
                back: {
                    display: false
                }
            },
            fileUploaderOptions: {
                deleteRequestOptions: {
                    headers: {
                        Authorization: `Bearer ${token}`
                    },
                    payload: {},
                    requestUrl: ({
                        questionId,
                        file
                    }) => {
                        return ``
                    }
                },
                uploadRequestOptions: {
                    headers: {
                        Authorization: `Bearer ${token}`
                    },
                    payload: {},
                    requestUrl: ({
                        questionId
                    }) => {
                        return ``
                    }
                }
            },
            questions: {
                showNumber: true
            }
        }
    }, [ 
        token, 
        index
    ]);

    const isQuestionnaireRegistered = controlQuestionnaireProgress.progressQuestionnaires.findIndex(q => q.id === report.case_id) !== -1;

    const questionnaireTabsProgressState = controlQuestionnaireProgress.progressQuestionnaires.find(q => q.id === report.case_id);

    const requiredQuestions = questionnaireTabsProgressState?.tabs.reduce((acc, tab) => {
        return acc + (tab.required || 0);
    }, 0) || 0;
    const requiredAnsweredQuestionsCount = questionnaireTabsProgressState?.tabs.reduce((acc, tab) => {
        return acc + (tab.requiredAnswered || 0);
    }, 0) || 0;

    const optionalQuestions = questionnaireTabsProgressState?.tabs.reduce((acc, tab) => {
        return acc + (tab.optional || 0);
    }, 0) || 0;
    const optionalAnsweredQuestionsCount = questionnaireTabsProgressState?.tabs.reduce((acc, tab) => {
        return acc + (tab.optionalAnswered || 0);
    }, 0) || 0;

    const answeredQuestionsCount =
        requiredAnsweredQuestionsCount + optionalAnsweredQuestionsCount;
    const questionsCount = requiredQuestions + optionalQuestions;
    const totalPercent = questionsCount
        ? Math.round((answeredQuestionsCount / questionsCount) * 100)
        : 0;

    useEffect(() => {
        onProgressChange && onProgressChange(
            report.case_id,
            {
                answered: answeredQuestionsCount,
                total: questionsCount,
                percent: totalPercent
            }
        );
    }, [
        answeredQuestionsCount,
        onProgressChange,
        questionsCount,
        report.case_id,
        totalPercent
    ]);

    const questionnaireComponents = useMemo(() => {
        const QuestionExtension = composeQuestionExtensions(
            createQuestionnaireFocusMarkersExtension(),
            createQuestionnaireAiSuggestionExtension(),
            createQuestionnaireAssessmentGapExtension(),
            createQuestionnaireGapExtension(
                buildComplianceObligationsMap(complianceObligations)
            )
        );

        return QuestionExtension ? { QuestionExtension } : undefined;
    }, [complianceObligations]);

    if(!isQuestionnaireRegistered) {
        return (
            <QuestionsLoadingPlaceholder />
        )
    }

    return (
        <div className="assessment-panel__questionnaire" ref={questionnaireRef}>
            <div className="assessment-panel__content-heading">
                <span className="assessment-panel__number">
                    {index + 1}
                </span>
                <strong>{report.module_name}</strong>
                <span
                    className={`assessment-panel__status ms-auto ${
                        questionnaireCompleted ? "completed" : "in-progress"
                    }`}
                >
                    {questionnaireCompleted ? t("Completed") : t("In Progress")}
                </span>
            </div>

            <SimpleQuestionnaire 
                questionnaireUniqueId={report.case_id}
                className='gdpr'
                questions={report.questions || []}
                defaultValues={report.mappedAnswers}
                settings={settings}
                components={questionnaireComponents}
                onQuestionnaireCompleteStatusChanged={questionnaireStatusChangeCallback}
                onSubmit={handleSubmitQuestionnaire}
                extraData={extraData}
                {...controlQuestionnaireProgress}
            />

            <AssessmentActions>
                <AssessmentSaveButton
                    isSaving={saveInProgress}
                    onClick={save}
                />
            </AssessmentActions>
        </div>
    );
}

const mapStatetoProps = (state) => {
	const { token, user } = state.Login;

	return {
		token,
		user
	};
};

export default withNamespaces()(
    connect(mapStatetoProps, {})(
        ProcessingQuestionnaire
    )
)